export interface ApoeIsotypeResult {
	isotype: string;
	oddsRatio: number;
	note: string;
}

// Clinical constant ported verbatim from gene.codes pubscripts1.js determineIsotype() — keys are
// `<rs7412 pair>_<rs429358 pair>`, so the rs7412 genotype comes FIRST despite the argument order.
const ISOTYPE_LOOKUP: Record<string, ApoeIsotypeResult> = {
	CT_CC: { isotype: "ε3/ε4", oddsRatio: 3.2, note: "3.2× risk vs average" },
	TC_CC: { isotype: "ε3/ε4", oddsRatio: 3.2, note: "3.2× risk vs average" },
	CC_CC: { isotype: "ε4/ε4", oddsRatio: 11.6, note: "11.6× risk vs average" },
	CC_CT: { isotype: "ε3/ε4", oddsRatio: 3.2, note: "3.2× risk vs average" },
	CC_TC: { isotype: "ε3/ε4", oddsRatio: 3.2, note: "3.2× risk vs average" },
	CC_TT: { isotype: "ε3/ε3", oddsRatio: 1.0, note: "average risk" },
	CT_TT: { isotype: "ε2/ε3", oddsRatio: 0.6, note: "40% reduced risk" },
	TC_TT: { isotype: "ε2/ε3", oddsRatio: 0.6, note: "40% reduced risk" },
	TT_TT: { isotype: "ε2/ε2", oddsRatio: 0.2, note: "80% reduced risk" },
	CT_CT: { isotype: "ε2/ε4", oddsRatio: 2.6, note: "2.6× risk vs average" },
	TC_CT: { isotype: "ε2/ε4", oddsRatio: 2.6, note: "2.6× risk vs average" },
	CT_TC: { isotype: "ε2/ε4", oddsRatio: 2.6, note: "2.6× risk vs average" },
};

const UNCALLED = ["NOAMP", "UND", ""];

const callAllele = (allele: unknown): string | null => {
	if (typeof allele !== "string") {
		return null;
	}

	const normalised = allele.trim().toUpperCase();

	return UNCALLED.indexOf(normalised) === -1 ? normalised : null;
};

const callPair = (pair: [string, string] | null | undefined): [string, string] | null => {
	if (!pair) {
		return null;
	}

	const first = callAllele(pair[0]);
	const second = callAllele(pair[1]);

	return first !== null && second !== null ? [first, second] : null;
};

// Returns null rather than guessing: an uncalled SNP or a genotype pair the legacy table never
// covered (e.g. rs7412 T/T with an rs429358 C) is not an isotype we can report.
export const apoeIsotype = (
	rs429358: [string, string],
	rs7412: [string, string],
): ApoeIsotypeResult | null => {
	const left = callPair(rs7412);
	const right = callPair(rs429358);

	if (!left || !right) {
		return null;
	}

	const leftKeys = [left[0] + left[1], left[1] + left[0]];
	const rightKeys = [right[0] + right[1], right[1] + right[0]];

	for (let i = 0; i < leftKeys.length; i++) {
		for (let j = 0; j < rightKeys.length; j++) {
			const hit = ISOTYPE_LOOKUP[leftKeys[i] + "_" + rightKeys[j]];

			if (hit) {
				return { ...hit };
			}
		}
	}

	return null;
};
